import { useEffect, useRef, type CSSProperties } from 'react'
import './healthAgeOrb.css'

const TARGET_AGE = 34.2
const COUNT_DURATION_MS = 1800
const COUNT_DELAY_MS = 1400
const PARTICLE_COUNTS = {
  default: { bokeh: 7, dots: 16, specks: 56 },
  small: { bokeh: 4, dots: 10, specks: 32 },
} as const

class AuroraBlob {
  angle: number
  orbit: number
  speed: number
  size: number
  phase: number
  pulseSpeed: number
  baseAlpha: number
  red: number
  green: number
  blue: number

  constructor(index: number, total: number, canvasSize: number) {
    this.angle = (index / total) * Math.PI * 2
    this.orbit = canvasSize * (0.15 + Math.random() * 0.2)
    this.speed = 0.0003 + Math.random() * 0.0004
    this.size = canvasSize * (0.2 + Math.random() * 0.25)
    this.phase = Math.random() * Math.PI * 2
    this.pulseSpeed = 0.001 + Math.random() * 0.001
    this.baseAlpha = 0.03 + Math.random() * 0.04
    this.red = 157
    this.green = 163
    this.blue = 175
  }

  draw(ctx: CanvasRenderingContext2D, elapsedTime: number, canvasSize: number) {
    const center = canvasSize / 2
    const x = center + Math.cos(this.angle + elapsedTime * this.speed) * this.orbit
    const y = center + Math.sin(this.angle + elapsedTime * this.speed * 0.7) * this.orbit * 0.8
    const alpha = this.baseAlpha * (0.6 + 0.4 * Math.sin(elapsedTime * this.pulseSpeed + this.phase))
    const currentSize = this.size * (0.85 + 0.15 * Math.sin(elapsedTime * this.pulseSpeed * 0.5 + this.phase))
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, currentSize)

    gradient.addColorStop(0, `rgba(${this.red}, ${this.green}, ${this.blue}, ${alpha})`)
    gradient.addColorStop(0.5, `rgba(${this.red}, ${this.green}, ${this.blue}, ${alpha * 0.4})`)
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')

    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(x, y, currentSize, 0, Math.PI * 2)
    ctx.fill()
  }
}

class SpeckParticle {
  x = 0
  y = 0
  size = 0
  orbitCenterX = 0
  orbitCenterY = 0
  orbitRadius = 0
  orbitSpeed = 0
  orbitAngle = 0
  twinkleSpeed = 0
  twinklePhase = 0
  baseAlpha = 0
  life = 0
  maxLife = 0

  reset(canvasSize: number, isInitialSeed: boolean) {
    const center = canvasSize / 2
    const angle = Math.random() * Math.PI * 2
    const radius = Math.random() * center * 0.92
    this.x = center + Math.cos(angle) * radius
    this.y = center + Math.sin(angle) * radius
    this.size = 0.7 + Math.random() * 1.2

    this.orbitCenterX = center + (Math.random() - 0.5) * center * 0.5
    this.orbitCenterY = center + (Math.random() - 0.5) * center * 0.5
    this.orbitRadius = 12 + Math.random() * 28
    this.orbitSpeed = (0.00045 + Math.random() * 0.0011) * (Math.random() < 0.5 ? 1 : -1)
    this.orbitAngle = Math.random() * Math.PI * 2

    this.twinkleSpeed = 0.0012 + Math.random() * 0.0024
    this.twinklePhase = Math.random() * Math.PI * 2
    this.baseAlpha = 0.22 + Math.random() * 0.36

    this.life = isInitialSeed ? Math.random() * 9000 : 5000 + Math.random() * 7000
    this.maxLife = this.life
  }

  update(deltaTime: number, canvasSize: number) {
    this.life -= deltaTime
    if (this.life <= 0) {
      this.reset(canvasSize, false)
      return
    }

    this.orbitAngle += this.orbitSpeed * deltaTime
    this.x = this.orbitCenterX + Math.cos(this.orbitAngle) * this.orbitRadius
    this.y = this.orbitCenterY + Math.sin(this.orbitAngle) * this.orbitRadius
  }

  draw(ctx: CanvasRenderingContext2D, elapsedTime: number) {
    const fadeTime = 800
    let lifeFade = 1

    if (this.life < fadeTime) {
      lifeFade = this.life / fadeTime
    }
    if (this.life > this.maxLife - fadeTime) {
      lifeFade = (this.maxLife - this.life) / fadeTime
    }

    const twinkle = 0.62 + 0.38 * (0.5 + 0.5 * Math.sin(elapsedTime * this.twinkleSpeed + this.twinklePhase))
    const alpha = this.baseAlpha * twinkle * lifeFade
    const currentSize = this.size * (0.82 + 0.18 * Math.sin(elapsedTime * this.twinkleSpeed * 0.8 + this.twinklePhase))

    ctx.beginPath()
    ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2)
    ctx.fillStyle = `rgba(243, 244, 246, ${alpha})`
    ctx.fill()
  }
}

class DotParticle {
  homeX = 0
  homeY = 0
  x = 0
  y = 0
  size = 0
  amplitudeX = 0
  amplitudeY = 0
  frequencyX = 0
  frequencyY = 0
  phaseX = 0
  phaseY = 0
  pulseSpeed = 0
  pulsePhase = 0
  baseAlpha = 0
  green = 0
  blue = 0
  life = 0
  maxLife = 0

  reset(canvasSize: number, isInitialSeed: boolean) {
    const center = canvasSize / 2
    const angle = Math.random() * Math.PI * 2
    const radius = center * 0.25 + Math.random() * center * 0.65

    this.homeX = center + Math.cos(angle) * radius
    this.homeY = center + Math.sin(angle) * radius
    this.x = this.homeX
    this.y = this.homeY
    this.size = 2.2 + Math.random() * 3.8

    this.amplitudeX = 8 + Math.random() * 20
    this.amplitudeY = 8 + Math.random() * 20
    this.frequencyX = 0.00035 + Math.random() * 0.00055
    this.frequencyY = 0.00045 + Math.random() * 0.00065
    this.phaseX = Math.random() * Math.PI * 2
    this.phaseY = Math.random() * Math.PI * 2

    this.pulseSpeed = 0.0011 + Math.random() * 0.0022
    this.pulsePhase = Math.random() * Math.PI * 2
    this.baseAlpha = 0.16 + Math.random() * 0.28

    this.life = isInitialSeed ? Math.random() * 11000 : 6000 + Math.random() * 9000
    this.maxLife = this.life
    this.green = 163
    this.blue = 175
  }

  update(deltaTime: number, elapsedTime: number, canvasSize: number) {
    this.life -= deltaTime
    if (this.life <= 0) {
      this.reset(canvasSize, false)
      return
    }

    this.x = this.homeX + Math.sin(elapsedTime * this.frequencyX + this.phaseX) * this.amplitudeX
    this.y = this.homeY + Math.cos(elapsedTime * this.frequencyY + this.phaseY) * this.amplitudeY
  }

  draw(ctx: CanvasRenderingContext2D, elapsedTime: number) {
    const fadeTime = 1000
    let lifeFade = 1

    if (this.life < fadeTime) {
      lifeFade = this.life / fadeTime
    }
    if (this.life > this.maxLife - fadeTime) {
      lifeFade = (this.maxLife - this.life) / fadeTime
    }

    const pulse = 0.58 + 0.42 * (0.5 + 0.5 * Math.sin(elapsedTime * this.pulseSpeed + this.pulsePhase))
    const alpha = this.baseAlpha * pulse * lifeFade
    const currentSize = this.size * (0.86 + 0.14 * Math.sin(elapsedTime * this.pulseSpeed * 1.2 + this.pulsePhase))
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, currentSize)

    gradient.addColorStop(0, `rgba(157, ${this.green}, ${this.blue}, ${alpha})`)
    gradient.addColorStop(0.55, `rgba(157, ${this.green}, ${this.blue}, ${alpha * 0.22})`)
    gradient.addColorStop(1, `rgba(157, ${this.green}, ${this.blue}, 0)`)
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2)
    ctx.fill()
  }
}

class BokehParticle {
  homeX = 0
  homeY = 0
  x = 0
  y = 0
  size = 0
  driftAmplitudeX = 0
  driftAmplitudeY = 0
  driftFrequencyX = 0
  driftFrequencyY = 0
  driftPhaseX = 0
  driftPhaseY = 0
  breatheSpeed = 0
  breathePhase = 0
  baseAlpha = 0
  highlightAngle = 0
  life = 0
  maxLife = 0

  reset(canvasSize: number, isInitialSeed: boolean) {
    const center = canvasSize / 2
    const angle = Math.random() * Math.PI * 2
    const radius = center * 0.4 + Math.random() * center * 0.6

    this.homeX = center + Math.cos(angle) * radius
    this.homeY = center + Math.sin(angle) * radius
    this.x = this.homeX
    this.y = this.homeY
    this.size = 12 + Math.random() * 28

    this.driftAmplitudeX = 10 + Math.random() * 24
    this.driftAmplitudeY = 10 + Math.random() * 24
    this.driftFrequencyX = 0.00014 + Math.random() * 0.00026
    this.driftFrequencyY = 0.00016 + Math.random() * 0.0003
    this.driftPhaseX = Math.random() * Math.PI * 2
    this.driftPhaseY = Math.random() * Math.PI * 2

    this.breatheSpeed = 0.00045 + Math.random() * 0.0012
    this.breathePhase = Math.random() * Math.PI * 2
    this.baseAlpha = 0.05 + Math.random() * 0.1
    this.highlightAngle = Math.random() * Math.PI * 2

    this.life = isInitialSeed ? Math.random() * 16000 : 9000 + Math.random() * 13000
    this.maxLife = this.life
  }

  update(deltaTime: number, elapsedTime: number, canvasSize: number) {
    this.life -= deltaTime
    if (this.life <= 0) {
      this.reset(canvasSize, false)
      return
    }

    this.x = this.homeX + Math.sin(elapsedTime * this.driftFrequencyX + this.driftPhaseX) * this.driftAmplitudeX
    this.y = this.homeY + Math.cos(elapsedTime * this.driftFrequencyY + this.driftPhaseY) * this.driftAmplitudeY
  }

  draw(ctx: CanvasRenderingContext2D, elapsedTime: number) {
    const fadeTime = 2000
    let lifeFade = 1

    if (this.life < fadeTime) {
      lifeFade = this.life / fadeTime
    }
    if (this.life > this.maxLife - fadeTime) {
      lifeFade = (this.maxLife - this.life) / fadeTime
    }

    const breathe = 0.9 + 0.1 * Math.sin(elapsedTime * this.breatheSpeed + this.breathePhase)
    const alpha = this.baseAlpha * breathe * lifeFade
    const currentSize = this.size * (0.92 + 0.08 * Math.sin(elapsedTime * this.breatheSpeed * 0.7 + this.breathePhase))
    const highlightX = this.x + Math.cos(this.highlightAngle) * currentSize * 0.25
    const highlightY = this.y + Math.sin(this.highlightAngle) * currentSize * 0.25
    const gradient = ctx.createRadialGradient(highlightX, highlightY, 0, this.x, this.y, currentSize)

    gradient.addColorStop(0, `rgba(157, 163, 175, ${alpha * 0.95})`)
    gradient.addColorStop(0.45, `rgba(157, 163, 175, ${alpha * 0.24})`)
    gradient.addColorStop(1, 'rgba(243, 244, 246, 0)')

    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2)
    ctx.fill()

    ctx.strokeStyle = `rgba(157, 163, 175, ${alpha * 0.16})`
    ctx.lineWidth = 0.8
    ctx.stroke()
  }
}

function easeOutCubic(progress: number) {
  return 1 - Math.pow(1 - progress, 3)
}

interface HealthAgeOrbProps {
  size?: 'default' | 'small'
}

export default function HealthAgeOrb({ size = 'default' }: HealthAgeOrbProps) {
  const orbRef = useRef<HTMLDivElement | null>(null)
  const shadeCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const particleCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const ageValueRef = useRef<HTMLDivElement | null>(null)
  const orbStyle: CSSProperties | undefined =
    size === 'small'
      ? ({ ['--health-orb-size' as const]: 'min(260px, 54vw)' } as CSSProperties)
      : undefined

  useEffect(() => {
    const particleCounts = size === 'small' ? PARTICLE_COUNTS.small : PARTICLE_COUNTS.default
    const orbElement = orbRef.current
    const shadeCanvas = shadeCanvasRef.current
    const particleCanvas = particleCanvasRef.current
    const ageValueElement = ageValueRef.current

    if (!orbElement || !shadeCanvas || !particleCanvas || !ageValueElement) {
      return
    }

    const shadeContext = shadeCanvas.getContext('2d')
    const particleContext = particleCanvas.getContext('2d')
    if (!shadeContext || !particleContext) {
      return
    }

    let canvasSize = 420
    let lastTime = 0
    let orbAnimationFrameId = 0
    let countAnimationFrameId = 0

    const getSize = () => orbElement.offsetWidth || 420
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const auroraBlobs: AuroraBlob[] = []
    const specks = Array.from({ length: particleCounts.specks }, () => {
      const particle = new SpeckParticle()
      particle.reset(canvasSize, true)
      return particle
    })
    const dots = Array.from({ length: particleCounts.dots }, () => {
      const particle = new DotParticle()
      particle.reset(canvasSize, true)
      return particle
    })
    const bokehs = Array.from({ length: particleCounts.bokeh }, () => {
      const particle = new BokehParticle()
      particle.reset(canvasSize, true)
      return particle
    })

    const reseedVisuals = () => {
      auroraBlobs.length = 0
      for (let index = 0; index < 7; index += 1) {
        auroraBlobs.push(new AuroraBlob(index, 7, canvasSize))
      }

      specks.forEach((particle) => particle.reset(canvasSize, true))
      dots.forEach((particle) => particle.reset(canvasSize, true))
      bokehs.forEach((particle) => particle.reset(canvasSize, true))
    }

    const resizeAll = () => {
      canvasSize = getSize()
      shadeCanvas.width = canvasSize
      shadeCanvas.height = canvasSize
      particleCanvas.width = canvasSize
      particleCanvas.height = canvasSize
      reseedVisuals()
    }

    const animate = (time: number) => {
      const deltaTime = lastTime ? time - lastTime : 16
      lastTime = time

      shadeContext.clearRect(0, 0, canvasSize, canvasSize)
      shadeContext.save()
      shadeContext.beginPath()
      shadeContext.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, Math.PI * 2)
      shadeContext.clip()
      auroraBlobs.forEach((blob) => blob.draw(shadeContext, time, canvasSize))
      shadeContext.restore()

      particleContext.clearRect(0, 0, canvasSize, canvasSize)
      particleContext.save()
      particleContext.beginPath()
      particleContext.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, Math.PI * 2)
      particleContext.clip()

      bokehs.forEach((particle) => {
        particle.update(deltaTime, time, canvasSize)
        particle.draw(particleContext, time)
      })
      dots.forEach((particle) => {
        particle.update(deltaTime, time, canvasSize)
        particle.draw(particleContext, time)
      })
      specks.forEach((particle) => {
        particle.update(deltaTime, canvasSize)
        particle.draw(particleContext, time)
      })

      particleContext.restore()
      orbAnimationFrameId = window.requestAnimationFrame(animate)
    }

    const startAgeCounter = () => {
      const counterStartTime = performance.now() + COUNT_DELAY_MS

      const updateCounter = (now: number) => {
        const elapsed = now - counterStartTime
        if (elapsed < 0) {
          countAnimationFrameId = window.requestAnimationFrame(updateCounter)
          return
        }

        const progress = Math.min(elapsed / COUNT_DURATION_MS, 1)
        const animatedAgeValue = easeOutCubic(progress) * TARGET_AGE
        ageValueElement.textContent = animatedAgeValue.toFixed(1)

        if (progress < 1) {
          countAnimationFrameId = window.requestAnimationFrame(updateCounter)
        }
      }

      countAnimationFrameId = window.requestAnimationFrame(updateCounter)
    }

    resizeAll()
    window.addEventListener('resize', resizeAll)

    if (prefersReducedMotion) {
      ageValueElement.textContent = TARGET_AGE.toFixed(1)
      return () => {
        window.removeEventListener('resize', resizeAll)
      }
    }

    orbAnimationFrameId = window.requestAnimationFrame(animate)
    startAgeCounter()

    return () => {
      window.removeEventListener('resize', resizeAll)
      window.cancelAnimationFrame(orbAnimationFrameId)
      window.cancelAnimationFrame(countAnimationFrameId)
    }
  }, [size])

  return (
    <section className="health-age-orb" style={orbStyle} aria-label="Health age visual">
      <div className="health-age-orb__scene">
        <div className="health-age-orb__orb" ref={orbRef}>
          <canvas ref={shadeCanvasRef} className="health-age-orb__shade-canvas" aria-hidden="true" />
          <div className="health-age-orb__shade-rotate-1" aria-hidden="true" />
          <div className="health-age-orb__shade-rotate-2" aria-hidden="true" />
          <div className="health-age-orb__nebula-1" aria-hidden="true" />
          <div className="health-age-orb__nebula-2" aria-hidden="true" />
          <div className="health-age-orb__nebula-3" aria-hidden="true" />
          <div className="health-age-orb__light-pool-1" aria-hidden="true" />
          <div className="health-age-orb__light-pool-2" aria-hidden="true" />
          <div className="health-age-orb__shape-gradient" aria-hidden="true" />
          <canvas ref={particleCanvasRef} className="health-age-orb__particle-canvas" aria-hidden="true" />
          <div className="health-age-orb__rim-glow" aria-hidden="true" />
          <div className="health-age-orb__rim-highlight" aria-hidden="true">
            <div className="health-age-orb__rim-highlight-inner" />
          </div>
          <div className="health-age-orb__text-layer">
            <div ref={ageValueRef} className="health-age-orb__age-value">
              0.0
            </div>
            <div className="health-age-orb__age-label">Health age</div>
            <div className="health-age-orb__age-diff">11.2 years younger</div>
          </div>
        </div>
      </div>
    </section>
  )
}
